import { useContext } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CacheRequestConfig } from 'axios-cache-interceptor';

import { ConfigContext } from '../../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../../aurr/contexts/LoginContext';
import { buildCacheKey, getAxiosInstance } from '../../../axiosCache';
import {
  KUDOS_MUTATE_SET_KEY,
  KUDOS_MUTATE_UNSET_KEY,
  KUDOS_QUERY_KEY,
  TOPIC_QUERY_KEY,
  USER_QUERY_KEY,
} from '../../constants';
import { KhorosTopicProps } from '../../interface/khoros.interface';
import { KhorosTopicSchema } from '../../type/khoros-topic.type';
import { KhorosUserSchema } from '../../type/khoros-user.type';
import { useKhorosUser } from '../useKhorosUser';

export const useKhorosKudoUnset = ({ topicId, baseURL }: KhorosTopicProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  const { access_token, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  // create axios client used in all community function requests
  const axiosConfig: CacheRequestConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
    cache: {
      update: {
        [buildCacheKey([KUDOS_QUERY_KEY, topicId, sub])]: 'delete',
        [buildCacheKey([USER_QUERY_KEY, sub])]: 'delete',
        [buildCacheKey([TOPIC_QUERY_KEY, topicId, sub])]: 'delete',
      },
    },
  };

  const { data: userResponse } = useKhorosUser({
    baseURL,
  });

  const api = getAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [KUDOS_MUTATE_UNSET_KEY, { topicId }],
    mutationFn: async () => {
      const res = await api.delete(
        `/services/v2/community/khoros/kudos?id=${topicId}`,
        axiosConfig
      );
      return res?.data || undefined;
    },
    onMutate: async () => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [KUDOS_MUTATE_SET_KEY, { topicId }],
      });
      await queryClient.cancelQueries({ queryKey: [USER_QUERY_KEY, { sub }] });
      await queryClient.cancelQueries({
        queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
      });

      // Snapshot the previous value
      const savedUser = queryClient.getQueryData([USER_QUERY_KEY, { sub }]);
      const savedTopic = queryClient.getQueryData([
        TOPIC_QUERY_KEY,
        { topicId, sub },
      ]);

      // Optimistically update to the new value
      const fakeUser: KhorosUserSchema = {
        status: 'temporary-value',
        kh_user_id: 'temporary-value',
        kh_user_login: 'temporary-value',
        kh_user_avatar: 'temporary-value',
        kh_kudos_count: 0,
        kh_messages_count: 0,
        statuscode: 0,
      };
      if (!userResponse) {
        queryClient.setQueryData<KhorosUserSchema>(
          [USER_QUERY_KEY, { sub }],
          () => {
            return fakeUser;
          }
        );
      }
      queryClient.setQueryData<KhorosTopicSchema>(
        [TOPIC_QUERY_KEY, { topicId, sub }],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            kh_kudos_count: (old.kh_kudos_count || 1) - 1,
            kh_context_user: {
              ...(old?.kh_context_user || {}),
              ...old.kh_context_user,
              kudoed: false,
            },
          };
        }
      );

      // Return a context object with the snapshotted value
      return { savedTopic, savedUser };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, newTodo, context) => {
      queryClient.setQueryData([USER_QUERY_KEY, { sub }], context?.savedUser);
      queryClient.setQueryData(
        [TOPIC_QUERY_KEY, { topicId, sub }],
        context?.savedTopic
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, { sub }] });
      queryClient.invalidateQueries({
        queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
      });
      queryClient.invalidateQueries({
        queryKey: [KUDOS_QUERY_KEY, { topicId, sub }],
      });
    },
  });
};
